import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { orderService } from '../services/orderService';
import { couponService } from '../services/couponService';
import { razorpayService } from '../services/razorpayService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';
import { Tag, Check, X, Smartphone, Building, MessageCircle } from 'lucide-react';
import { UPI_ID, BANK_DETAILS } from '../utils/constants';
import LocateViaLiveMap from '../components/LocateViaLiveMap';
import { useAuth } from '../context/AuthContext';

const Checkout = () => {
  const { cart, clearCart, giftPacking } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);

  // 1. Read selected item IDs passed from Shopping Cart page
  const checkoutItemIds = location.state?.checkoutItems || [];

  // 2. Filter cart to ONLY include items checked in the Shopping Cart
  const checkoutCart = checkoutItemIds.length > 0
    ? cart.filter(item => checkoutItemIds.includes(item._id || item.id))
    : cart;

  // 3. Calculate total strictly based on filtered items
  const checkoutCartTotal = checkoutCart.reduce(
    (sum, item) => sum + item.price * item.quantity, 0
  );

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [discount, setDiscount] = useState(0);

  // Payment state
  const [showUPIPayment, setShowUPIPayment] = useState(false);
  const [showBankTransfer, setShowBankTransfer] = useState(false);
  const [showRazorpayPayment, setShowRazorpayPayment] = useState(false);
  const [paymentRecords, setPaymentRecords] = useState([]);

  const [coordinates, setCoordinates] = useState(null);

  const [formData, setFormData] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
    paymentMethod: 'Razorpay'
  });

  const [useSavedAddress, setUseSavedAddress] = useState(true);

  // Auto-fill user address on load
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        street: user.address || user.street || prev.street,
        city: user.city || prev.city,
        state: user.state || prev.state,
        zipCode: user.pincode || user.zipCode || prev.zipCode,
        country: user.country || 'India',
      }));
    }
  }, [user]);

  const shippingPrice = checkoutCartTotal > 100 ? 0 : 10;
  const taxPrice = Math.round((checkoutCartTotal - discount) * 0.18);
  const giftPackingPrice = giftPacking ? 50 : 0;
  const totalPrice = Math.round(checkoutCartTotal - discount + shippingPrice + taxPrice + giftPackingPrice);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'paymentMethod') {
      setShowUPIPayment(value === 'UPI');
      setShowBankTransfer(value === 'Bank Transfer');
      setShowRazorpayPayment(value === 'Razorpay');
    }
  };

  // Load Razorpay checkout script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleRazorpayPayment = async () => {
    setLoading(true);
    try {
      const amountInPaise = Math.round(totalPrice * 100);

      if (amountInPaise < 100) {
        toast.error('Minimum order amount is Rs 1 for Razorpay');
        setLoading(false);
        return;
      }

      const orderResponse = await razorpayService.createOrder(amountInPaise, 'INR');

      if (!orderResponse.success) {
        toast.error(orderResponse.message || 'Failed to create payment order');
        setLoading(false);
        return;
      }

      const { order_id, amount, currency } = orderResponse.data;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_TGZz3yqrxHZyTa',
        amount: amount,
        currency: currency,
        name: 'ProNest',
        description: 'Order Payment - Rs ' + totalPrice,
        order_id: order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await razorpayService.verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );

            if (verifyResponse.success && verifyResponse.data.isPaid) {
              const orderData = {
                orderItems: checkoutCart.map((item) => ({
                  product: item._id || item.id,
                  name: item.name,
                  quantity: item.quantity,
                  image: item.image,
                  price: item.price
                })),
                shippingAddress: {
                  street: formData.street,
                  city: formData.city,
                  state: formData.state,
                  zipCode: formData.zipCode,
                  country: formData.country,
                  coordinates: coordinates ? { lat: coordinates.lat, lng: coordinates.lng } : null
                },
                paymentMethod: 'Razorpay',
                paymentResult: {
                  id: response.razorpay_payment_id,
                  status: 'completed',
                  updateTime: new Date().toISOString()
                },
                itemsPrice: Math.round(checkoutCartTotal),
                taxPrice,
                shippingPrice,
                totalPrice,
                couponCode: appliedCoupon?.code || null,
                couponDiscount: discount,
                discountType: appliedCoupon?.discountType || null,
                isPaid: true,
                paidAt: Date.now(),
                paymentRecords: [
                  {
                    paymentType: 'Razorpay',
                    transactionId: response.razorpay_payment_id,
                    amount: totalPrice,
                    status: 'completed',
                    timestamp: new Date(),
                    notes: 'Razorpay Payment - Order ID: ' + response.razorpay_order_id
                  }
                ],
                giftPacking: giftPacking,
                giftPackingPrice: giftPackingPrice
              };

              const createdOrder = await orderService.createOrder(orderData);
              toast.success('Payment successful! Order placed.');
              clearCart();
              navigate('/payment-status', {
                state: {
                  status: 'success',
                  orderData: createdOrder.data || orderData
                }
              });
            } else {
              toast.error('Payment verification failed. Please contact support.');
            }
          } catch (verifyError) {
            toast.error(verifyError.response?.data?.message || 'Payment verification failed');
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            toast.error('Payment cancelled. Your order was not placed.');
            setLoading(false);
          }
        },
        prefill: {
          name: user?.name || user?.username || '',
          email: user?.email || '',
          contact: user?.phone || user?.mobile || ''
        },
        theme: {
          color: '#7C3AED'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error('Payment failed: ' + response.error.description);
        setLoading(false);
      });
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    try {
      const response = await couponService.validateCoupon(couponCode, checkoutCartTotal);
      if (response.success) {
        setAppliedCoupon(response.data);

        let discountAmount = 0;
        if (response.data.discountType === 'percentage') {
          discountAmount = (checkoutCartTotal * response.data.discountValue) / 100;
          if (response.data.maximumDiscount && discountAmount > response.data.maximumDiscount) {
            discountAmount = response.data.maximumDiscount;
          }
        } else {
          discountAmount = response.data.discountValue;
        }

        setDiscount(Math.round(discountAmount));
        toast.success('Coupon applied successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid coupon code');
      setAppliedCoupon(null);
      setDiscount(0);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setDiscount(0);
    toast.success('Coupon removed');
  };

  const handleUPIPayment = () => {
    const upiLink =
      'upi://pay?pa=' +
      UPI_ID +
      '&pn=ProNest&am=' +
      Math.round(totalPrice) +
      '&cu=INR&tn=Order Payment';
    window.location.href = upiLink;

    const record = {
      paymentType: 'UPI',
      transactionId: 'UPI-' + Date.now(),
      amount: totalPrice,
      status: 'pending',
      timestamp: new Date(),
      notes: 'UPI payment initiated - Please complete payment in your UPI app'
    };

    setPaymentRecords([...paymentRecords, record]);
    toast.success('UPI app opened! Please complete the payment.');
  };

  const handleBankTransferConfirm = () => {
    const record = {
      paymentType: 'Bank Transfer',
      transactionId: 'BT-' + Date.now(),
      amount: totalPrice,
      status: 'pending',
      timestamp: new Date(),
      notes: 'Bank transfer initiated - Please transfer to the account below'
    };

    setPaymentRecords([...paymentRecords, record]);
    toast.success('Bank transfer details shown. Please transfer the amount.');
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (checkoutCart.length === 0) {
      toast.error('No items selected for checkout. Please go back to cart.');
      return;
    }

    if (!formData.street || !formData.city || !formData.state || !formData.zipCode || !formData.country) {
      toast.error('Please fill out all required shipping address fields.');
      return;
    }

    if (formData.paymentMethod === 'Razorpay') {
      await handleRazorpayPayment();
      return;
    }

    setLoading(true);

    const orderData = {
      orderItems: checkoutCart.map((item) => ({
        product: item._id || item.id,
        name: item.name,
        quantity: item.quantity,
        image: item.image,
        price: item.price
      })),
      shippingAddress: {
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
        coordinates: coordinates ? { lat: coordinates.lat, lng: coordinates.lng } : null
      },
      paymentMethod: formData.paymentMethod,
      itemsPrice: Math.round(checkoutCartTotal),
      taxPrice,
      shippingPrice,
      totalPrice,
      couponCode: appliedCoupon?.code || null,
      couponDiscount: discount,
      discountType: appliedCoupon?.discountType || null,
      paymentRecords: paymentRecords,
      giftPacking: giftPacking,
      giftPackingPrice: giftPackingPrice
    };

    if (formData.paymentMethod === 'UPI') {
      orderData.upiPaymentStatus = 'pending';
      orderData.upiTransactionId = 'UPI-' + Date.now();
    }

    if (formData.paymentMethod === 'Bank Transfer') {
      orderData.bankPaymentStatus = 'pending';
      orderData.bankTransactionId = 'BT-' + Date.now();
    }

    try {
      await orderService.createOrder(orderData);
      toast.success('Order placed successfully!');
      clearCart();
      navigate('/store');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Checkout</h1>

        {user?.address && (
          <div className="mb-6 bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-purple-100 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={useSavedAddress}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUseSavedAddress(checked);

                  if (checked) {
                    setFormData((prev) => ({
                      ...prev,
                      street: user.address || user.street || prev.street,
                      city: user.city || prev.city,
                      state: user.state || prev.state,
                      zipCode: user.pincode || user.zipCode || prev.zipCode,
                      country: user.country || 'India'
                    }));
                  }
                }}
                className="mt-1 accent-purple-600"
              />
              <div>
                <p className="text-sm font-semibold text-gray-800">Use saved user details</p>
                <p className="text-xs text-gray-500 mt-1">
                  {user.name || user.email} | {user.address}{' '}
                  {user.pincode ? '- ' + user.pincode : ''}
                </p>
              </div>
            </label>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-6">
              <LocateViaLiveMap
                title="Shipping Address"
                formData={formData}
                onFormDataChange={setFormData}
                coordinates={coordinates}
                onCoordinatesChange={setCoordinates}
              />

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  >
                    <option value="Razorpay">Razorpay (Card / UPI / Wallet)</option>
                    <option value="Credit / Debit Card">Credit / Debit Card</option>
                    <option value="Cash on Delivery">Cash on Delivery</option>
                    <option value="UPI">UPI (Instant Payment)</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                  </select>
                </div>

                {showUPIPayment && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Smartphone className="text-green-600" size={24} />
                      <h3 className="font-bold text-green-800">UPI Payment</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Pay to UPI ID:</p>
                        <p className="font-mono font-bold text-lg text-green-700">{UPI_ID}</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg flex flex-col items-center">
                        <p className="text-sm text-gray-600 mb-2">Scan QR Code to Pay:</p>
                        <img
                          src={
                            'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=' +
                            encodeURIComponent(
                              'upi://pay?pa=' +
                                UPI_ID +
                                '&pn=ProNest&am=' +
                                Math.round(totalPrice) +
                                '&cu=INR&tn=Order Payment'
                            )
                          }
                          alt="UPI QR Code"
                          className="w-40 h-40"
                        />
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          Scan with any UPI app
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Amount to Pay:</p>
                        <p className="font-bold text-xl text-purple-700">
                          Rs {Math.round(totalPrice)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleUPIPayment}
                        className="w-full bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 transition-all font-medium flex items-center justify-center gap-2"
                      >
                        <Smartphone size={20} />
                        Pay with UPI App
                      </button>
                    </div>
                  </div>
                )}

                {showBankTransfer && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Building className="text-blue-600" size={24} />
                      <h3 className="font-bold text-blue-800">Bank Transfer Details</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Bank Name:</p>
                        <p className="font-medium text-lg text-blue-700">
                          {BANK_DETAILS.bankName}
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Account Number:</p>
                        <p className="font-mono font-bold text-lg">{BANK_DETAILS.accountNumber}</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-sm text-gray-600">IFSC Code:</p>
                        <p className="font-mono font-bold text-lg">{BANK_DETAILS.ifscCode}</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Amount to Transfer:</p>
                        <p className="font-bold text-xl text-purple-700">
                          Rs {Math.round(totalPrice)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleBankTransferConfirm}
                        className="w-full bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-all font-medium flex items-center justify-center gap-2"
                      >
                        <Check size={20} />
                        I've Transferred the Amount
                      </button>
                      <div className="mt-4 p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-800 font-medium mb-2">
                          After bank transfer, please share payment details on WhatsApp
                        </p>
                        <a
                          href={
                            'https://wa.me/917010929678?text=Hello,%20I%20have%20made%20a%20bank%20transfer%20for%20my%20order.%20Please%20confirm.%20Order%20Total:%20Rs%20' +
                            Math.round(totalPrice)
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full py-2 px-4 bg-green-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-green-700 transition-colors"
                        >
                          <MessageCircle size={18} />
                          Share on WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>

              <div className="mb-4 p-4 bg-purple-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Tag size={18} className="text-purple-600" />
                  <span className="font-medium text-gray-800">Have a coupon?</span>
                </div>

                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-100 p-2 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Check size={16} className="text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        {appliedCoupon.code}
                      </span>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      className="flex-1 px-3 py-2 border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                    >
                      {couponLoading ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>

              {/* Displaying ONLY selected checkout items */}
              <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
                {checkoutCart.map((item, idx) => (
                  <div key={item._id || item.id || idx} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.name} x {item.quantity}
                    </span>
                    <span className="font-medium">
                      Rs {Math.round((item.price || 0) * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal:</span>
                  <span className="font-medium">Rs {Math.round(checkoutCartTotal)}</span>
                </div>

                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount:</span>
                    <span className="font-medium">-Rs {Math.round(discount)}</span>
                  </div>
                )}

                <div className="flex justify-between text-gray-600">
                  <span>Shipping:</span>
                  <span className="font-medium">Rs {Math.round(shippingPrice)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tax (18%):</span>
                  <span className="font-medium">Rs {Math.round(taxPrice)}</span>
                </div>
                {giftPacking && (
                  <div className="flex justify-between text-purple-600">
                    <span>Gift Packing:</span>
                    <span className="font-medium">Rs {Math.round(giftPackingPrice)}</span>
                  </div>
                )}

                <div className="border-t pt-3 flex justify-between text-xl font-bold text-gray-800 mb-4">
                  <span>Total:</span>
                  <span className="text-purple-600">Rs {Math.round(totalPrice)}</span>
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || checkoutCart.length === 0}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center justify-center"
                >
                  {loading ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>

              {paymentRecords.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-bold text-gray-800 mb-2">Payment Records</h3>
                  {paymentRecords.map((record, index) => (
                    <div
                      key={index}
                      className="text-sm border-b border-gray-200 py-2 last:border-0"
                    >
                      <div className="flex justify-between">
                        <span className="font-medium">{record.paymentType}</span>
                        <span className="text-purple-600">Rs {Math.round(record.amount)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>ID: {record.transactionId}</span>
                        <span
                          className={
                            record.status === 'pending' ? 'text-yellow-600' : 'text-green-600'
                          }
                        >
                          {record.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Checkout;